/**
 * Staff Portal Service Hooks
 * React hooks for accessing staff portal services (staff HR self-service)
 */
import { useCCMS } from '../useCCMS';
/**
 * Hook to access all staff portal services
 * @returns StaffPortalServices object containing all staff portal service instances
 */
export function useStaffPortalServices() {
    const { services } = useCCMS();
    if (!services?.staffPortal) {
        throw new Error('Staff Portal services not available. Ensure CCMSProvider is configured with staffPortal services.');
    }
    return services.staffPortal;
}
/**
 * Hook to access StaffPortalLeaveService
 * Used for leave request operations (list, create, update, cancel)
 * - Staff can manage their own leave requests
 * - Only pending leaves (StatusID=1) can be updated
 * - Cancel sets StatusID to -2
 */
export function useStaffPortalLeaveService() {
    return useStaffPortalServices().leaves;
}
/**
 * Hook to access StaffPortalLeaveDocumentService
 * Used for leave document management
 * - Staff can upload/view/delete documents on their own leave requests
 * - Ownership validated via parent leave
 */
export function useStaffPortalLeaveDocumentService() {
    return useStaffPortalServices().leaveDocuments;
}
/**
 * Hook to access StaffPortalProfileService
 * Used for staff profile operations
 * - Staff can view their full profile
 * - Staff can only update limited fields (JobTitle, EmployeeNo)
 * - Cannot update TypeID, StatusID, StartDate, EndDate
 */
export function useStaffPortalProfileService() {
    return useStaffPortalServices().profile;
}
/**
 * Hook to access StaffPortalFileService
 * Used for employee file management
 * - Staff can view/upload/delete their own files
 * - HROnly files are restricted from staff access
 * - Uploaded files automatically have HROnly=false
 */
export function useStaffPortalFileService() {
    return useStaffPortalServices().files;
}
/**
 * Hook to access StaffPortalPolicyAssignmentService
 * - List my pending / signed assignments
 * - Sign assignments (captures IP/UA server-side)
 */
export function useStaffPortalPolicyAssignmentService() {
    return useStaffPortalServices().policyAssignments;
}
/**
 * Hook to access StaffPortalAnnouncementService
 * - Read-only feed of published HR announcements visible to the user
 */
export function useStaffPortalAnnouncementService() {
    return useStaffPortalServices().announcements;
}
/**
 * Hook to access StaffPortalCalendarService
 * - Birthdays (privacy-safe)
 * - Who's on approved leave today (privacy-safe)
 */
export function useStaffPortalCalendarService() {
    return useStaffPortalServices().calendar;
}
/**
 * Hook to access StaffPortalLookupsService
 * - Read-only lookups (leave types, statuses, colleagues, etc.)
 */
export function useStaffPortalLookupsService() {
    return useStaffPortalServices().lookups;
}
/**
 * Hook to access StaffPortalDutyService
 * - My assigned duties + the duty catalogue
 */
export function useStaffPortalDutyService() {
    return useStaffPortalServices().duties;
}
/**
 * Hook to access StaffPortalLeaveDutyService — leave-duty associations
 * (suggesting which duties to cover during leave).
 */
export function useStaffPortalLeaveDutyService() {
    return useStaffPortalServices().leaveDuties;
}
/**
 * Hook to access StaffPortalPolicyService — read-only policy library
 * (browse policies the user has access to).
 */
export function useStaffPortalPolicyService() {
    return useStaffPortalServices().policies;
}
//# sourceMappingURL=useStaffPortalServices.js.map